<template>
  <div class="ai-chat">
    <div v-if="has2dPreview" class="ai-preview-banner">
      <span class="ai-preview-banner-text">
        {{ $t('aiDesign.viewing2d') }}
      </span>
      <button class="ai-back-to-3d" @click="$emit('backTo3d')">
        {{ $t('aiDesign.backTo3d') }}
      </button>
    </div>

    <div class="ai-messages" ref="messagesEl">
      <p v-if="chatMessages.length === 0" class="ai-empty-state">
        {{ $t('aiDesign.chatEmptyState') }}
      </p>
      <div
        v-for="(msg, i) in chatMessages"
        :key="i"
        class="ai-msg-row"
        :class="`ai-msg-row--${msg.role}`"
      >
        <div class="ai-bubble">{{ msg.text }}</div>
      </div>
    </div>

    <div class="ai-input-area">
      <div v-if="error" class="ai-error">{{ error }}</div>
      <div class="ai-input-row">
        <textarea
          v-model="inputText"
          class="ai-input"
          :placeholder="$t('aiDesign.placeholder')"
          rows="2"
          :disabled="loading"
          @keydown.enter.ctrl.prevent="send"
          @keydown.enter.meta.prevent="send"
        />
        <button
          class="ai-send-btn"
          @click="send"
          :disabled="loading || !inputText.trim()"
        >
          <span v-if="loading" class="ai-loading-dots">
            <span /><span /><span />
          </span>
          <span v-else>{{ $t('aiDesign.send') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick } from "vue";

const props = defineProps({
  sendFn: { type: Function, required: true },
  has2dPreview: { type: Boolean, default: false },
});

defineEmits(["backTo3d"]);

const inputText = ref("");
const loading = ref(false);
const error = ref("");
const messagesEl = ref(null);
const chatMessages = ref([]);

async function send() {
  const text = inputText.value.trim();
  if (!text || loading.value) return;

  chatMessages.value.push({ role: "user", text });
  inputText.value = "";
  error.value = "";
  loading.value = true;
  await scrollToBottom();

  try {
    const result = await props.sendFn(text);
    const message = result?.message || "";
    if (message) {
      chatMessages.value.push({ role: "assistant", text: message });
    }
  } catch (e) {
    error.value = e.message || "Failed to process request. Please try again.";
    chatMessages.value.pop();
  } finally {
    loading.value = false;
    await scrollToBottom();
  }
}

async function scrollToBottom() {
  await nextTick();
  if (messagesEl.value) {
    messagesEl.value.scrollTop = messagesEl.value.scrollHeight;
  }
}
</script>

<style scoped lang="scss">
.ai-chat {
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-top: 8px;
}

.ai-preview-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  margin-bottom: 8px;
  border-radius: 6px;
  background: rgba(76, 175, 80, 0.12);
  border: 1px solid rgba(76, 175, 80, 0.35);
  flex-shrink: 0;
}

.ai-preview-banner-text {
  color: var(--color-white, #fff);
  font-size: 12px;
  line-height: 1.3;
}

.ai-back-to-3d {
  padding: 5px 10px;
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-white, #fff);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 5px;
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
}

.ai-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0 8px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.ai-empty-state {
  font-size: 12px;
  color: var(--color-text-muted, #888);
  text-align: center;
  padding: 24px 8px;
  line-height: 1.6;
  margin: auto 0;
}

.ai-msg-row {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &--user {
    align-items: flex-end;

    .ai-bubble {
      background: var(--color-gree300, #4caf50);
      color: #000;
      border-radius: 12px 12px 3px 12px;
    }
  }

  &--assistant {
    align-items: flex-start;

    .ai-bubble {
      background: var(--color-dark-bg-primary, #1e1e1e);
      color: var(--color-white);
      border-radius: 3px 12px 12px 12px;
      border: 1px solid rgba(255, 255, 255, 0.07);
    }
  }
}

.ai-bubble {
  max-width: 88%;
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.55;
  word-break: break-word;
  white-space: pre-wrap;
}

.ai-input-area {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 4px;
}

.ai-error {
  color: #e55;
  font-size: 12px;
}

.ai-input-row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.ai-input {
  flex: 1;
  background: var(--color-dark-bg-primary, #1e1e1e);
  border: 1px solid var(--color-border, #333);
  border-radius: 6px;
  color: var(--color-white);
  font-size: 13px;
  padding: 8px 10px;
  resize: none;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;

  &::placeholder {
    color: var(--color-text-muted, #888);
  }

  &:focus {
    border-color: var(--color-primary, #4caf50);
  }

  &:disabled {
    opacity: 0.5;
  }
}

.ai-send-btn {
  flex-shrink: 0;
  padding: 9px 14px;
  background: var(--color-gree300, #4caf50);
  color: #000;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    opacity: 0.85;
  }
}

.ai-loading-dots {
  display: inline-flex;
  gap: 4px;
  align-items: center;

  span {
    width: 5px;
    height: 5px;
    background: #000;
    border-radius: 50%;
    animation: ai-bounce 1s infinite;

    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.30s; }
  }
}

@keyframes ai-bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}
</style>
